import { Table, Typography } from '@faceup/ui-base'
import {
  BCP47Language,
  ChannelType,
  type Country,
  convertCountryToCountryName,
  generateShortIdUrl,
  HotlineOrderStatus,
  translateLanguageName,
} from '@faceup/utils'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import { ManageHotlineModal } from './ManageHotlineModal'

const fragments = {
  ReportSourceHotlineTable_hotline: graphql(`
    fragment ReportSourceHotlineTable_hotline on ReportSource {
      id
      name
      sourceType
      order {
        id
        status
      }
      defaultLanguage
      languages
      isEnabled
      shortLink
      config {
        ... on AutomatedHotlineReportSourceConfiguration {
          id
          phoneNumber
          phoneNumberType
          automatedHotlineCountry: country
        }
        ... on AiHotlineReportSourceConfiguration {
          id
          phoneNumber
          phoneNumberType
          aiHotlineCountry: country
        }
        ... on LiveHotlineReportSourceConfiguration {
          id
          phoneNumber
          liveHotlineCountry: country
        }
      }
    }
  `),
}

type Props = {
  reportSources: FragmentType<typeof fragments.ReportSourceHotlineTable_hotline>[]
  title: string
  loading: boolean
  refetch: () => void
}

export const ReportSourcesHotlineTable = ({
  reportSources: _reportSources,
  loading,
  refetch,
  title,
}: Props) => {
  const reportSources = getFragmentData(fragments.ReportSourceHotlineTable_hotline, _reportSources)

  if (reportSources.length === 0) {
    return <Typography.Text>This institution does not have a {title} yet.</Typography.Text>
  }

  return (
    <Table
      loading={loading}
      rowKey='id'
      dataSource={reportSources}
      locale={{ emptyText: ' ' }}
      scroll={{ x: 'max-content' }}
      columns={[
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
        },
        {
          title: 'Type',
          key: 'phoneNumberType',
          render: reportSource =>
            reportSource?.config?.__typename === 'AutomatedHotlineReportSourceConfiguration' ||
            reportSource?.config?.__typename === 'AiHotlineReportSourceConfiguration'
              ? reportSource.config.phoneNumberType
              : null,
        },
        {
          title: 'Is enabled',
          key: 'isEnabled',
          render: ({ isEnabled }) => <>{isEnabled ? 'Yes' : 'No'}</>,
        },
        {
          title: 'Order status',
          key: 'status',
          render: reportSource => reportSource.order?.status,
        },
        {
          title: 'Phone number',
          key: 'phoneNumber',
          render: reportSource =>
            reportSource?.config?.__typename === 'AutomatedHotlineReportSourceConfiguration' ||
            reportSource?.config?.__typename === 'AiHotlineReportSourceConfiguration' ||
            reportSource?.config?.__typename === 'LiveHotlineReportSourceConfiguration'
              ? reportSource.config.phoneNumber
              : null,
        },
        {
          title: 'Country',
          key: 'country',
          render: reportSource => {
            const country =
              reportSource?.config?.__typename === 'AutomatedHotlineReportSourceConfiguration'
                ? reportSource.config.automatedHotlineCountry
                : reportSource?.config?.__typename === 'AiHotlineReportSourceConfiguration'
                  ? reportSource.config.aiHotlineCountry
                  : reportSource?.config?.__typename === 'LiveHotlineReportSourceConfiguration'
                    ? reportSource.config.liveHotlineCountry
                    : null
            return country ? convertCountryToCountryName(country as Country) : null
          },
        },
        {
          title: 'Languages',
          key: 'languages',
          render: row => (
            <>
              {row.languages.map(language => (
                <Typography.Text key={language} strong={language === row.defaultLanguage}>
                  {translateLanguageName(language, BCP47Language.en_US)}
                </Typography.Text>
              ))}
            </>
          ),
        },
        {
          title: 'Short link',
          key: 'shortLink',
          render: ({ shortLink }) =>
            shortLink ? (
              <a
                target='_blank'
                href={generateShortIdUrl(import.meta.env.VITE_SHORTLINK_URL ?? '', shortLink, null)}
                rel='noreferrer noopener'
              >
                {generateShortIdUrl(import.meta.env.VITE_SHORTLINK_URL ?? '', shortLink, null)}
              </a>
            ) : null,
        },
        {
          title: 'Actions',
          key: 'approve',
          width: '10%',
          render: reportSource => {
            const isProcessing = reportSource.order?.status === HotlineOrderStatus.Processing
            const isAutomatedOrAiHotline =
              reportSource.sourceType === ChannelType.AutomatedHotline ||
              reportSource.sourceType === ChannelType.AiHotline
            const shouldShowActions =
              isAutomatedOrAiHotline ||
              (isProcessing && reportSource.sourceType === ChannelType.LiveHotline)

            const getCountry = (): Country => {
              if (
                reportSource?.config?.__typename === 'AutomatedHotlineReportSourceConfiguration'
              ) {
                // By God's grace, may this match
                return reportSource.config.automatedHotlineCountry as unknown as Country
              }
              if (reportSource?.config?.__typename === 'AiHotlineReportSourceConfiguration') {
                // By God's grace, may this match
                return reportSource.config.aiHotlineCountry as unknown as Country
              }
              if (reportSource?.config?.__typename === 'LiveHotlineReportSourceConfiguration') {
                return reportSource.config.liveHotlineCountry
              }
              throw new Error('Country not found')
            }

            if (shouldShowActions) {
              const hotlineType =
                reportSource.sourceType === ChannelType.AutomatedHotline ||
                reportSource.sourceType === ChannelType.AiHotline ||
                reportSource.sourceType === ChannelType.LiveHotline
                  ? reportSource.sourceType
                  : null

              if (!hotlineType) {
                return null
              }

              const channelPhoneNumber =
                reportSource?.config?.__typename === 'AutomatedHotlineReportSourceConfiguration' ||
                reportSource?.config?.__typename === 'AiHotlineReportSourceConfiguration' ||
                reportSource?.config?.__typename === 'LiveHotlineReportSourceConfiguration'
                  ? reportSource.config.phoneNumber
                  : null

              return (
                <ManageHotlineModal
                  name={reportSource.name}
                  type={hotlineType}
                  channelId={reportSource.id}
                  refetch={refetch}
                  country={getCountry()}
                  phoneNumber={channelPhoneNumber}
                  isProcessing={isProcessing}
                />
              )
            }
            return null
          },
        },
      ]}
      pagination={false}
    />
  )
}
